/**
 * File utilities — MIME/extension-to-icon maps, prettyBytes, extension extraction,
 * download-safe filename sanitization.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/files.js
 * - packages/hoi-poi-ui/src/utils/prettyBytes.js
 * - apps/ssm-web4/src/app/utils/files/index.js
 *
 * `prettyBytes` uses the full-featured Sindre Sorhus implementation from
 * hoi-poi-ui (supports bits, binary, signed, locale, fraction digits).
 */

// ---------------------------------------------------------------------------
// Icon mappings
// ---------------------------------------------------------------------------

/** MIME type → icon name */
export const FILE_TYPES_ICON_MAP = {
    'image/bmp': 'img',
    'image/gif': 'img',
    'image/jpeg': 'img',
    'image/png': 'img',
    'image/svg+xml': 'img',
    'image/tiff': 'img',
    'image/webp': 'img',
    'image/heic': 'img',
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'doc',
    'application/vnd.oasis.opendocument.text': 'doc',
    'text/csv': 'xls',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xls',
    'application/vnd.oasis.opendocument.spreadsheet': 'xls',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'ppt',
    'application/vnd.oasis.opendocument.presentation': 'ppt',
    'text/html': 'html',
    'text/plain': 'txt',
    'application/zip': 'zip',
    'application/gzip': 'zip',
    'application/x-rar-compressed': 'zip',
    'application/x-7z-compressed': 'zip',
    'video/x-msvideo': 'mov',
    'video/mpeg': 'mov',
    'video/ogg': 'mov',
    'video/webm': 'mov',
    'video/mp4': 'mov',
};

/** File extension (lowercase, no dot) → icon name — union of web5 + web4 mappings. */
export const FILE_EXTENSIONS_ICON_MAP = {
    bmp: 'img',
    gif: 'img',
    jpeg: 'img',
    jpg: 'img',
    png: 'img',
    svg: 'img',
    tiff: 'img',
    tif: 'img',
    webp: 'img',
    heic: 'img',
    pdf: 'pdf',
    doc: 'doc',
    docx: 'doc',
    odt: 'doc',
    csv: 'xls',
    xls: 'xls',
    xlsx: 'xls',
    ods: 'xls',
    ppt: 'ppt',
    pptx: 'ppt',
    odp: 'ppt',
    html: 'html',
    txt: 'txt',
    zip: 'zip',
    rar: 'zip',
    gz: 'zip',
    '7z': 'zip',
    mp4: 'mov',
    avi: 'mov',
    mov: 'mov',
    mpeg: 'mov',
    webm: 'mov',
    // web4-only additions
    mp3: 'mp3',
    app: 'app',
    mdb: 'mdb',
    sql: 'mdb',
    pub: 'pub',
    vsd: 'vsd',
    bdd: 'bdd',
};

/**
 * Get the appropriate icon name for a file based on its MIME type or extension.
 *
 * @param {File|Object} file — object with `type` and/or `name` properties
 * @returns {string} Icon name (e.g. 'pdf', 'doc', 'img', 'file')
 */
export const getFileIcon = (file) => {
    if (file?.type && FILE_TYPES_ICON_MAP[file.type]) {
        return FILE_TYPES_ICON_MAP[file.type];
    }
    if (file?.name) {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (extension && FILE_EXTENSIONS_ICON_MAP[extension]) {
            return FILE_EXTENSIONS_ICON_MAP[extension];
        }
    }
    return 'file';
};

/**
 * Extract the file extension from a filename (lowercase, without the dot).
 *
 * @param {string} filename
 * @returns {string}
 */
export const getFileExtension = (filename) => {
    if (!filename || typeof filename !== 'string') return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

/**
 * Normalize a string for use as a browser download filename (lowercase ASCII slug, max length).
 *
 * @param {string} name
 * @returns {string}
 */
export const sanitizeFilename = (name) => {
    if (!name || typeof name !== 'string') return 'no-name';
    return name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
};

// ---------------------------------------------------------------------------
// prettyBytes  (Sindre Sorhus - hoi-poi-ui version, full-featured)
// ---------------------------------------------------------------------------

const BYTE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
const BIBYTE_UNITS = ['B', 'kiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
const BIT_UNITS = ['b', 'kbit', 'Mbit', 'Gbit', 'Tbit', 'Pbit', 'Ebit', 'Zbit', 'Ybit'];
const BIBIT_UNITS = ['b', 'kibit', 'Mibit', 'Gibit', 'Tibit', 'Pibit', 'Eibit', 'Zibit', 'Yibit'];

const toLocaleString = (number, locale, options) => {
    let result = number;
    if (typeof locale === 'string' || Array.isArray(locale)) {
        result = number.toLocaleString(locale, options);
    } else if (locale === true || options !== undefined) {
        result = number.toLocaleString(undefined, options);
    }
    return result;
};

/**
 * Format a number of bytes into a human-readable string.
 *
 * @param {number} number — byte count (must be finite)
 * @param {Object} [options]
 * @param {boolean} [options.bits=false] — show in bits
 * @param {boolean} [options.binary=false] — use binary units (kiB, MiB…)
 * @param {boolean} [options.signed=false] — always show sign
 * @param {string|boolean} [options.locale] — locale for formatting
 * @param {number} [options.minimumFractionDigits]
 * @param {number} [options.maximumFractionDigits]
 * @returns {string}
 */
export function prettyBytes(number, options) {
    if (!Number.isFinite(number)) {
        throw new TypeError(`Expected a finite number, got ${typeof number}: ${number}`);
    }

    options = {
        bits: false,
        binary: false,
        ...options,
    };

    const UNITS = options.bits
        ? options.binary
            ? BIBIT_UNITS
            : BIT_UNITS
        : options.binary
          ? BIBYTE_UNITS
          : BYTE_UNITS;

    if (options.signed && number === 0) {
        return ` 0 ${UNITS[0]}`;
    }

    const isNegative = number < 0;
    const prefix = isNegative ? '-' : options.signed ? '+' : '';

    if (isNegative) {
        number = -number;
    }

    let localeOptions;

    if (options.minimumFractionDigits !== undefined) {
        localeOptions = { minimumFractionDigits: options.minimumFractionDigits };
    }

    if (options.maximumFractionDigits !== undefined) {
        localeOptions = { maximumFractionDigits: options.maximumFractionDigits, ...localeOptions };
    }

    if (number < 1) {
        const numberString = toLocaleString(number, options.locale, localeOptions);
        return prefix + numberString + ' ' + UNITS[0];
    }

    const exponent = Math.min(
        Math.floor(options.binary ? Math.log(number) / Math.log(1024) : Math.log10(number) / 3),
        UNITS.length - 1,
    );
    number /= (options.binary ? 1024 : 1000) ** exponent;

    if (!localeOptions) {
        number = number.toPrecision(3);
    }

    const numberString = toLocaleString(Number(number), options.locale, localeOptions);
    const unit = UNITS[exponent];

    return prefix + numberString + ' ' + unit;
}
